import { Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { deliverAnalysis, useAnalysisAction, useAnalysisType, type AnalysisStaff, type OutputDecl } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { objectName } from '@/features/objects/objectName';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';

export function DeliverModal({ analysis, opened, onClose }: { analysis: AnalysisStaff; opened: boolean; onClose: () => void }) {
  const { t } = useTranslation('analyses');
  const { t: tc } = useTranslation();
  const kindLabel = useKindLabel();
  const { data: type } = useAnalysisType(analysis.type_key);
  const deliverable = new Set(((type?.outputs as OutputDecl[] | undefined) ?? []).filter((o) => o.deliverable).map((o) => o.kind));
  const outputs = (analysis.all_outputs ?? []).filter((o) => !o.deleted);
  const deliver = useAnalysisAction(deliverAnalysis);
  const go = async () => {
    try {
      await deliver.mutateAsync({ id: analysis.public_id, args: [] });
      notifications.show({ color: 'forest', message: t('deliver.done') });
      onClose();
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };
  return (
    <Modal opened={opened} onClose={onClose} title={t('deliver.title')}>
      <Stack gap="md">
        <Text fz={14}>{t('deliver.hint')}</Text>
        <List fz={14}>
          {outputs.map((o) => (
            <List.Item key={o.public_id}>
              {kindLabel(o.kind)} · {objectName(o)}{' '}
              <Text component="span" fz={12} c={deliverable.has(o.kind) ? 'forest.6' : brand.textMuted}>
                {deliverable.has(o.kind) ? t('deliver.willShow') : t('deliver.staysInternal')}
              </Text>
            </List.Item>
          ))}
        </List>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {tc('common.cancel')}
          </Button>
          <Button loading={deliver.isPending} onClick={() => void go()}>
            {t('deliver.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
